/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosResponseHeaders,
} from "axios";
import set from "lodash/set";

import { deleteLocalData } from "./services/localData/tokenActions";
import { AUTH_PATHS } from "../pages/auth/routes/constants";
import { fetchAuthSession } from "aws-amplify/auth";
import { showErrorNotify } from "../utils/notify";
import { usersRequests } from "./services/usersRequests/usersRequests";
import { API_URL, IS_INACTIVITY_LOGOUT_DISABLED } from "../constants/env";

interface IAxiosInstanceProps {
  baseURL?: string;
  isPrivate?: boolean;
  withHeadersData?: boolean;
  useOldAccessToken?: boolean;
}

const DEFAULT_HEADERS = {
  "Content-Type": "application/json; charset=utf-8",
  Accept: "application/json",
  "Access-Control-Allow-Origin": "*",
  "Access-Control-Allow-Methods": "GET, POST, PATCH, PUT, DELETE, OPTIONS",
  "Access-Control-Allow-Headers": "Origin, Content-Type, X-auth-Token",
  timeoutErrorMessage: "timeout",
  timeout: 2000,
};

const requestParser = <T>(result: AxiosResponse<T>): T => result.data;
const requestWithHeaders = <T>(
  result: AxiosResponse<T>
): { data: T; headers: AxiosResponseHeaders } => ({
  data: result.data,
  headers: result.headers,
});
const errorHandler = <T>(error: AxiosError<T>): Promise<never> =>
  Promise.reject(error?.response);

const requestHandler = async (
  config: AxiosRequestConfig<any>
): Promise<any> => {

  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken!;

  // handle auto log out
  const epochNow = Math.floor(new Date().getTime() / 1000);
  
  if (accessToken) {
    if (
      IS_INACTIVITY_LOGOUT_DISABLED ||
      usersRequests.getAutoLogoutExpiry() > epochNow
    ) {
      
      // user currently active - slide expiration
      if (!IS_INACTIVITY_LOGOUT_DISABLED) {
        usersRequests.slideAutoLogoutExpiry();
      }

      const bearerToken = `Bearer ${accessToken}`;
      set(config, "headers.Authorization", bearerToken);

    } else {
      await usersRequests.logout(undefined, "You have been signed out due to inactivity.");
    }
   }

  const facilityKey = localStorage.getItem('facilityKey');
  if (facilityKey) {
    set(config, "headers.FacilityKey", facilityKey);
  }
  
  return config;
};

const axiosInstance = (props?: IAxiosInstanceProps): AxiosInstance => {

  const baseURL = props?.baseURL || API_URL;
  const isPrivate = props?.isPrivate || false;
  const withHeadersData = props?.withHeadersData || false;

  const instance = axios.create({ baseURL, headers: DEFAULT_HEADERS });

  if (isPrivate) {
    instance.interceptors.request.use((config) =>
      requestHandler(config)
    );
  }

  instance.interceptors.request.use((request) => request, errorHandler);

  instance.interceptors.response.use(
    withHeadersData ? requestWithHeaders : requestParser,
    (error) =>
      // Return only needed props: "status", "data"
      // eslint-disable-next-line prefer-promise-reject-errors
    {

      if(error.response?.status === 401) {
        deleteLocalData();
        window.location.href = `${AUTH_PATHS.AUTH}${AUTH_PATHS.LOGIN}`;
        return;

      } else if (error.response?.status === 403) {
        console.log("UNAUTHORIZED REQUEST: ", error.response.config.url);
        showErrorNotify("Unauthorized");
        return Promise.reject({
          status: error.response?.status,
          data: error.response?.data,
        })
      } else {
        return Promise.reject({
          status: error.response?.status,
          data: error.response?.data,
        })
    }
  }
  );

  return instance;
};

export const apiPrivateAxios = axiosInstance({ isPrivate: true });

export const apiPrivateAxiosWithBaseURL = axiosInstance({
  isPrivate: true,
  baseURL: "",
});

export const apiPrivateAxiosWithHeaders = axiosInstance({
  isPrivate: true,
  withHeadersData: true,
});

// Use old "accessToken" for Authorization (e.g. for DELETE token method )
export const apiPrivateAxiosUseOldAccessToken = axiosInstance({
  isPrivate: true,
  useOldAccessToken: true,
});

export const apiPublicAxios = axiosInstance();

const axiosInstances = { apiPublicAxios, apiPrivateAxios, apiPrivateAxiosWithBaseURL };

export default axiosInstances;
